<template>
  <transition name="t-xp-bar">
    <div v-show="show" class="xp-bar-container bg-ui-panel" @click="hide">
      <div class="pos-relative z2 d-flex align-center py-1 px-2">
        <span class="text-h6 mr-2">Lvl {{ currentLevel }}</span>
        <span class="text-body-1 green200--text">
          (<span ref="xpText" />xp)
        </span>
        <v-spacer />
        <span class="text-body-2 green500--text">+{{ xpGained | toLocaleInt }}xp</span>
      </div>
      <div ref="xpBar" class="progress-bar z1" />
    </div>
  </transition>
</template>

<script>
import anime from 'animejs/lib/anime.es'
import { LEVELS } from '@/utils/constants'
import { sleep } from '@/utils'
import { registerSound } from '@/modules/sound'

const sounds = {
  levelUp: registerSound('level_up', { volume: 0.4 }),
}

const getLevel = xp => {
  const nextLevel = LEVELS.findIndex(lvl => lvl > xp)
  return (nextLevel !== -1 ? nextLevel : LEVELS.length) - 1
}

const getLevelProgress = (xp, currentLevelXp, nextLevelXp) => (xp - currentLevelXp) / (nextLevelXp - currentLevelXp)

const intl = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export default {
  components: {},
  data: () => ({
    show: false,
    currentAnimation: null,
    currentLevel: 0,
    xpGained: 0,
    queue: [],
  }),
  created() {
    this.$bus.$on('user.xp', this.onReceive)
  },
  methods: {
    async onReceive({ oldXp, xp }) {
      // clean up queue if not active
      if (!this.show) {
        this.queue = []
      }

      const startXp = oldXp
      const endXp = oldXp + xp
      const startLevel = getLevel(startXp)
      const endLevel = getLevel(endXp)

      // skip if max level
      if (startLevel === LEVELS.length - 1) {
        return
      }

      this.show = true
      await this.$nextTick()

      this.xpGained = xp

      if (!this.queue.length) {
        this.currentLevel = startLevel
      }

      console.log(`User is going from ${startXp}xp (lvl ${startLevel}) --> ${endXp}xp (lvl ${endLevel})`)

      const anims = this.generateAnimations(startXp, endXp)

      // if already running
      if (this.queue.length) {
        this.queue = [...this.queue, ...anims]
        return
      }

      this.queue = anims
      this.processQueue()
    },
    generateAnimations(startXp, endXp, anims = []) {
      const nextLevel = getLevel(startXp) + 1
      const nextLevelXp = LEVELS?.[nextLevel] ?? 0

      const animEndXp = Math.min(endXp, nextLevelXp)
      anims.push(this.generateAnimation(startXp, animEndXp, endXp >= nextLevelXp))

      // we go agane
      if (endXp > nextLevelXp) {
        return this.generateAnimations(nextLevelXp, endXp, anims)
      }

      return anims
    },
    generateAnimation(startXp, endXp, isLevelUp) {
      const { xpText, xpBar } = this.$refs

      const startLevel = getLevel(startXp)
      const startLevelXp = LEVELS?.[startLevel] ?? 0
      const nextLevelXp = LEVELS?.[startLevel + 1] ?? 0

      const startProgress = getLevelProgress(startXp, startLevelXp, nextLevelXp)
      const endProgress = getLevelProgress(endXp, startLevelXp, nextLevelXp)

      const anim = anime.timeline({
        autoplay: false,
        duration: 3e3,
        easing: 'easeInOutQuad',
      })

      anim
        .add({
          targets: { xp: startXp },
          xp: endXp,
          round: 1,
          begin: () => {
            this.currentLevel = getLevel(startXp)
          },
          update: ({ animations }) => {
            const { currentValue } = animations[0]
            if (xpText) {
              xpText.innerText = intl.format(currentValue)
            }
          },
        })
        .add({
          targets: xpBar,
          scaleX: [startProgress, endProgress],
        }, 0)

      if (isLevelUp) {
        anim.add({
          targets: '.xp-bar-container',
          translateY: [
            { value: 0, duration: 0 },
            { value: -20, duration: 200 },
            { value: 0, duration: 200 },
          ],
          translateX: ['-50%', '-50%'],
          duration: 500,
          easing: 'easeOutBack',
          begin: () => {
            sounds.levelUp.play()
            this.currentLevel = getLevel(endXp)
          },
          complete: animation => {
            animation.remove('.xp-bar-container')
            this.$el.removeAttribute('style')
          },

        }, '-=200')
      }

      return anim
    },
    async processQueue() {
      if (!this.queue.length) {
        await sleep(300)
        this.show = false
        return
      }

      const [anim] = this.queue
      this.currentAnimation = anim
      anim.play()

      await anim.finished
      this.queue.shift()

      this.processQueue()
    },
    hide() {
      if (this.currentAnimation) {
        this.currentAnimation.pause()
      }

      this.queue = []
      this.show = false
    },
  },
}
</script>

<style lang="scss" scoped>
.xp-bar-container {
  position: fixed;
  z-index: 999;
  bottom: 20px;
  left: 50%;
  width: 20%;
  min-width: 300px;
  max-width: 400px;
  transition: transform 0.5s cubic-bezier(0, 1.2, 0.5, 1.3);
  transform: translateX(-50%);
}

.progress-bar {
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(green(500), 0.2);
  transform-origin: center left;
}

.t-xp-bar {
  &-enter,
  &-leave-to {
    transform: translateY(150%) translateX(-50%);
  }

  &-enter-to {
    transform: translateY(0) translateX(-50%);
  }
}
</style>
